import { Component, OnInit, ViewChild } from '@angular/core'
import { LoaderService } from '@core/services/loader.service'
import { AppService } from '@app/general/services/app.service'
import { Pagination } from '@core/classes/pagination'
import { SignaComponent } from '@app/general/classes/signa-component'
import { LanguageService } from '@core/services/language.service'
import { finalize, forkJoin } from 'rxjs'
import {
	ContractorRead,
	ContractorReadList,
	ContractorsService,
	CostInvoiceItemRead,
	CostInvoiceRead,
	EntityRead,
	EntityReadList,
	InvoicesService,
	OrderMinimalData,
	PaymentStatusRead,
	SortOrder,
} from '@app/generated'
import { calculateSum } from '@app/invoices/utils/calculate-sum'
import { calculateNetValueFromUnitNetPrice } from '@app/invoices/utils/calculate-net-value-from-unit-net-price'
import { calculateTaxValueFromNetValue } from '@app/invoices/utils/calculate-tax-value-from-net-value'
import { calculateDivision } from '@app/invoices/utils/calculate-division'
import { InvoiceItemRead } from '@app/generated/model/invoiceItemRead'
import { Router } from '@angular/router'
import { config } from '@config'
import { NotificationService } from '@core/services/notification.service'
import { ModalService } from '@core/services/modal.service'
import { CreateBasedOnTheCostComponent } from '@app/cost-invoices/components/create-based-on-the-cost/create-based-on-the-cost.component'
import { ModalComponent } from '@core/components/modal/modal.component'
import { Permission } from '@app/acl/enums/permissions.enum'
import { CostInvoiceUpdateStatusComponent } from '@app/cost-invoices/pages/cost-invoice-update-status/cost-invoice-update-status.component'
import { FormControl, FormGroup } from '@angular/forms'
import { calculateDifference } from '@app/invoices/utils/calculate-difference'
import { CurrencyCode } from '@core/enums/currency.enum'
import { AssignCostToOrder } from '@app/cost-invoices/components/assign-cost-to-order/assign-cost-to-order.component'
import { TableComponent } from '@core/components/table/table.component'
import { TableQuery } from '@core/types/table-query'
import { NullableDate, NullableString } from '@core/types/nullable'
import { LayoutService } from '@core/services/layout.service'
import { ScrollItem } from '@app/orders/components/order-list/order-list.component'
import { formatDate } from '@app/general/utils/format-date'
import { setDecimalPlaces } from '@app/invoices/utils/set-decimal-places'

export const CostInvoiceFiltersLocalStorageKey = 'cost-invoice-view-list-filters'
export const CostInvoiceListScrollStorageKey = 'cost-invoice-view-list-scroll'

type SortBy = 'created_at' | 'updated_at' | 'deleted_at' | 'document_number'

interface CostInvoiceTotalsCacheEntry {
	net: number
	gross: number
	isPredicted?: boolean
	predictedGross?: number
	difference?: number
}

interface CostInvoiceFiltersFormScheme {
	search: FormControl<NullableString>
	hasInvoices: FormControl<NullableString>
	predictedCost: FormControl<NullableString>
	entity: FormControl<EntityRead[] | null>
	contractor: FormControl<ContractorRead[] | null>
	paymentStatusID: FormControl<NullableString>
	issueDateFrom: FormControl<NullableDate>
	issueDateTo: FormControl<NullableDate>
	orderCost: FormControl<NullableString>
	isPaidCash: FormControl<boolean | null>
	sentToAccounting: FormControl<boolean | null>
}

export type CostInvoiceFiltersValues = {
	[P in keyof CostInvoiceFiltersFormScheme]: CostInvoiceFiltersFormScheme[P]['value']
}

export type CostInvoiceFiltersAndSearchValues = {
	filters: CostInvoiceFiltersValues | undefined
	sortBy?: SortBy
	sortOrder?: 'asc' | 'desc'
	perPage?: number
	pageNumber?: number
}

@Component({
	selector: 'app-cost-invoices',
	templateUrl: './cost-invoices.page.html',
	styleUrls: ['./cost-invoices.page.scss', './../../../invoices/invoices.module.scss'],
})
export class CostInvoicesPage extends SignaComponent implements OnInit {
	@ViewChild('tableComponent') tableComponent?: TableComponent
	items: Array<CostInvoiceRead> = []
	pagination = new Pagination()
	languageCode = this.languageService.getSelectedLanguage
	filtersFormGroup!: FormGroup<CostInvoiceFiltersFormScheme>
	summaryCurrency: CurrencyCode = CurrencyCode.EUR
	contractors: ContractorReadList = { total: 0, items: [] }
	entities: EntityReadList = { total: 0, items: [] }
	sortOrderFormControl: FormControl<'desc' | 'asc' | null> = new FormControl(null)
	sortByFormControl: FormControl<SortBy | null> = new FormControl(null)
	isLoaded: boolean = false

	dateControl = new FormControl(new Date())
	isPaidCashControl = new FormControl(false)

	expandedRows: { [id: string]: boolean } = {}
	paymentStatuses: PaymentStatusRead[] = []
	scrollY: number = 0

	protected readonly textRemoveFilters = $localize`Clear filters`
	protected readonly textListOfCostInvoices = $localize`List of cost invoices`
	protected readonly textIssueAnInvoice = $localize`Add a cost invoice`
	protected readonly textSearch = $localize`Search`
	protected readonly textEnterSearch = $localize`Enter phrase`
	protected readonly textAddIncome = $localize`Add income`
	protected readonly textPreviewCostInvoice = $localize`Preview cost invoice`
	protected readonly textEditCostInvoice = $localize`Edit cost invoice`
	protected readonly textDeleteCostInvoice = $localize`Delete cost invoice`
	protected readonly textDeleteCostInvoices = $localize`Delete cost invoices`
	protected readonly textPreview = $localize`This is prediction for cost invoice`
	protected readonly textGroupInvoice = $localize`Group invoice`
	protected readonly textCoverageInRevenues = $localize`Coverage in revenues`
	protected readonly textInvoicesMeetingThePredictedCost = $localize`Invoice meeting the predicted cost`
	protected readonly textChangeSummaryCurrency = $localize`Change summary currency`
	protected readonly textContractorGroup = $localize`Contractor group`
	protected readonly textEntity = $localize`Contractor`
	protected readonly textSearchRecipient = $localize`Search`
	protected readonly textAssignCost = $localize`Assign cost to order`
	protected readonly textUnassignCost = $localize`Unassign cost from order`
	protected readonly textPreviewShowAll = $localize`Show all`
	protected readonly textPreviewShowLess = $localize`Show less`
	protected readonly textPaymentStatus = $localize`Payment status`
	protected readonly textShowAll = $localize`Show all`
	protected readonly textAssignationGroupInvoice = $localize`Change assignation of group cost invoice`
	protected readonly textEnterPaymentDate = $localize`Enter payment date`
	protected readonly textSelect = $localize`Select`
	protected readonly textDateOfIssue = $localize`Date of issue`
	protected readonly textFrom = $localize`From`
	protected readonly textTo = $localize`To`
	protected readonly textProformaCostInvoice = $localize`Proforma cost invoice`
	protected readonly textPredictions = $localize`Predictions`
	protected readonly textCostInvoiceOrders = $localize`Orders`
	protected readonly textCash = $localize`Cash`
	protected readonly textSentToAccounting = $localize`Sent to accounting`
	protected readonly textPaidWithCash = $localize`Paid with cash`
	protected readonly textMarkAsSentToAccounting = $localize`Mark as sent to accounting`
	protected readonly textConfirmSentToAccounting = $localize`Are you sure you want to mark this invoice as sent to accounting?`
	protected readonly textCashPayment = $localize`Cash payment`
	protected readonly textYes = $localize`Yes`
	protected readonly textNo = $localize`No`
	protected readonly textConfirmUnmarkSentToAccounting = $localize`Are you sure you want to unmark this invoice as sent to accounting?`
	protected readonly textUnmarkSentToAccounting = $localize`Unmark sent to accounting`
	protected readonly textBulkSendToAccounting = $localize`Send to accounting`
	protected readonly textConfirmBulkSendToAccounting = $localize`Are you sure you want to mark selected invoices as sent to accounting?`
	protected readonly textNoInvoicesSelected = $localize`No invoices selected`
	protected readonly textInvoicesMarkedAsSentToAccounting = $localize`Invoices marked as sent to accounting`
	protected readonly textFailedToMarkInvoicesAsSentToAccounting = $localize`Failed to mark invoices as sent to accounting`

	private selectedInvoices: CostInvoiceRead[] = []

	// Performance caches to avoid heavy recomputation on each change detection
	private totalsCache: Map<string, CostInvoiceTotalsCacheEntry> = new Map()
	private sumsByCurrency: Map<CurrencyCode, { net: number; gross: number }> = new Map()
	private filtersValue = this.filtersFormGroup?.value

	constructor(
		private loaderService: LoaderService,
		private appService: AppService,
		private languageService: LanguageService,
		private invoicesService: InvoicesService,
		private router: Router,
		private notificationService: NotificationService,
		private modalService: ModalService,
		private contractorService: ContractorsService,
		private layoutService: LayoutService,
	) {
		super()
		this.appService.title = $localize`Cost documents`
	}

	get availableCurrencies(): CurrencyCode[] {
		return Array.from(new Set(this.items.map(i => i.currency as CurrencyCode)))
	}

	get getLocalStorageFilterAndSortInvoice(): CostInvoiceFiltersAndSearchValues | undefined {
		const storedFilters = sessionStorage.getItem(CostInvoiceFiltersLocalStorageKey)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	get getSelectedPaymentStatus(): PaymentStatusRead | undefined {
		return this.paymentStatuses.find(i => i.id === this.filtersFormGroup.controls.paymentStatusID.value)
	}

	get getLocalStorageScroll(): ScrollItem | undefined {
		const storedFilters = sessionStorage.getItem(CostInvoiceListScrollStorageKey)

		if (!storedFilters) {
			return undefined
		}

		return JSON.parse(storedFilters)
	}

	ngOnInit(): void {
		this.pagination.setPerPageOptions([5, 10, 25, 50, 100, 200, 300, 400, 500, 1000])
		this.initFilters()
		const filters = this.getLocalStorageFilterAndSortInvoice
		this.filtersValue = JSON.parse(JSON.stringify(this.filtersFormGroup.value))
		const scroll = this.getLocalStorageScroll

		if (scroll?.scrollY) {
			const interval = setInterval(() => {
				if (this.isLoaded) {
					this.layoutService.layoutContainer?.nativeElement.scrollTo({
						top: scroll.scrollY ?? 0,
					})
					clearInterval(interval)
				}
			}, 300)
		}

		this.pagination.currentPageChanges().subscribe(() => {
			if (this.isLoaded) {
				sessionStorage.removeItem(CostInvoiceListScrollStorageKey)
			}
		})

		this.layoutService.afterScroll.subscribe(event => {
			const target = event!.target as HTMLElement

			if (target) {
				this.scrollY = target.scrollTop
			}
		})

		if (filters?.filters) {
			this.filtersFormGroup.controls.search.setValue('')
			this.filtersFormGroup.patchValue(filters.filters)
			this.sortByFormControl.patchValue((filters.sortBy as SortBy) ?? '')
			this.sortOrderFormControl.patchValue((filters.sortOrder as SortOrder) ?? '')
			this.pagination.setPerPage(filters.perPage ?? 25)
		}
		this.fetchContractors()
		this.fetchEntities()
		this.pagination.listShouldBeReloaded().subscribe(() => {
			this.fetchData()
		})
	}

	/**
	 * Calculates the net value of an invoice based on the given list of invoice items.
	 *
	 * @param {InvoiceItemRead[]} items - The list of invoice items.
	 *
	 * @return {number} - The calculated net value of the invoice.
	 */
	getNetValue(items: InvoiceItemRead[] | CostInvoiceItemRead[]): number {
		const components: number[] = []

		for (const item of items) {
			components.push(
				calculateNetValueFromUnitNetPrice({
					netPrice: item.net_unit_price ?? 0,
					quantity: item.quantity ?? 1,
				}),
			)
		}

		return calculateSum(components)
	}

	/**
	 * Calculates the total tax value for a list of invoice items.
	 *
	 * @param {InvoiceItemRead[]} items - The list of invoice items.
	 * @returns {number} The total tax value.
	 */
	getTaxValue(items: InvoiceItemRead[] | CostInvoiceItemRead[]): number {
		const components: number[] = []

		for (const item of items) {
			const netValue = calculateNetValueFromUnitNetPrice({
				netPrice: item.net_unit_price ?? 0,
				quantity: item.quantity ?? 1,
			})
			if (item.tax_rate?.value !== undefined && item.tax_rate?.value !== null) {
				components.push(
					calculateTaxValueFromNetValue({
						netValue: netValue,
						taxRate: calculateDivision(item.tax_rate.value, 100),
					}),
				)
			}
		}

		return calculateSum(components)
	}

	/**
	 * Calculates the gross value of an invoice.
	 *
	 * @param {InvoiceItemRead[]} items - An array of invoice items.
	 * @return {number} The sum of the net value and tax value of the invoice items.
	 */
	getGrossValue(items: InvoiceItemRead[] | CostInvoiceItemRead[]): number {
		const netValue = this.getNetValue(items)
		const taxValue = this.getTaxValue(items)

		return calculateSum([netValue, taxValue])
	}

	netValueOf(invoice: CostInvoiceRead): number {
		return this.getTotals(invoice).net
	}

	grossValueOf(invoice: CostInvoiceRead): number {
		return this.getTotals(invoice).gross
	}

	predictedGrossOf(invoice: CostInvoiceRead): number {
		const totals = this.getTotals(invoice)
		return totals.predictedGross ?? 0
	}

	differenceOf(invoice: CostInvoiceRead): number {
		const totals = this.getTotals(invoice)
		return totals.difference ?? 0
	}

	isPredictedOf(invoice: CostInvoiceRead): boolean {
		return !!this.getTotals(invoice).isPredicted
	}

	showPredictedWarnOf(invoice: CostInvoiceRead): boolean {
		if (!this.isPredictedOf(invoice) || invoice.is_predicted) {
			return false
		}
		return this.differenceOf(invoice) !== 0
	}

	predictedTooltipOf(costInvoice: CostInvoiceRead): string {
		const predicted = this.predictedGrossOf(costInvoice)
		const actual = this.grossValueOf(costInvoice)
		const str = $localize`The predicted costs do not match the actual costs!

The costs were predicted to be at the level of ${predicted} ${costInvoice.currency.toUpperCase()}, 
but the actual costs are ${actual} ${costInvoice.currency.toUpperCase()}.`
		const diff = this.differenceOf(costInvoice)
		const str2 =
			diff >= 0
				? $localize`\n\nThe invoice value is greater than predicted by ${diff} ${costInvoice.currency.toUpperCase()}.`
				: $localize`\n\nThe invoice value is less than predicted by ${diff * -1} ${costInvoice.currency.toUpperCase()}.`

		return str + str2
	}

	/**
	 * Calculate the sum of the net values of all items.
	 *
	 * @returns {number} The sum of the net values.
	 */
	getNetValueSum(): number {
		this.ensureCurrencySums(this.summaryCurrency)
		return this.sumsByCurrency.get(this.summaryCurrency)?.net ?? 0
	}

	/**
	 * Calculates the sum of the tax values of all items in the list.
	 *
	 * @return {number} The sum of the tax values.
	 */
	getTaxValueSum(): number {
		const components: number[] = []

		for (const item of this.items) {
			if (item.items) {
				components.push(this.getTaxValue(item.items))
			}
		}

		return calculateSum(components)
	}

	/**
	 * Calculates the sum of the gross values of all items in the items array.
	 *
	 * @returns {number} The sum of the gross values.
	 */
	getGrossValueSum(): number {
		this.ensureCurrencySums(this.summaryCurrency)
		return this.sumsByCurrency.get(this.summaryCurrency)?.gross ?? 0
	}

	aclCanCostInvoicesRead(): boolean {
		return this.aclService.hasPermission([Permission.COST_INVOICES_READ])
	}

	aclCanCostInvoicesCreate(): boolean {
		return this.aclService.hasPermission([Permission.COST_INVOICES_CREATE])
	}

	aclCanCostInvoicesUpdate(): boolean {
		return this.aclService.hasPermission([Permission.COST_INVOICES_UPDATE])
	}

	aclCanCostInvoicesDelete(): boolean {
		return this.aclService.hasPermission([Permission.COST_INVOICES_DELETE])
	}

	/**
	 * Creates a cost invoice.
	 *
	 * @return {Promise<void>} - A promise that resolves when the cost invoice creation is completed.
	 */
	async createCostInvoice(): Promise<void> {
		this.loaderService.enable(async (): Promise<void> => {
			await this.router.navigate(['/cost-invoices/create'])
		})
	}

	async deleteAll(): Promise<void> {
		if (this.selectedInvoices.length) {
			;(await this.modalService.confirmation()).subscribe((value: boolean) => {
				if (value) {
					const request = this.selectedInvoices.map(elem => this.invoicesService.deleteCostInvoice(elem.id))
					this.loaderService.setContain().enable(() => {
						forkJoin(request)
							.pipe(finalize(() => this.pagination.reloadList()))
							.subscribe({
								error: () => {
									this.notificationService.error($localize`Failed to delete all the indicated cost invoices.`)
								},
							})
					})
				}
			})
		} else {
			this.notificationService.error($localize`Requires selecting one or more cost invoices.`)
		}
	}

	async deleteOne(invoiceId: string): Promise<void> {
		const confirmation = await this.modalService.confirmation()
		confirmation.subscribe(async (value: boolean) => {
			if (value) {
				this.loaderService.setContain().enable(async () => {
					this.invoicesService
						.deleteCostInvoice(invoiceId.toString())
						.pipe(finalize(() => this.pagination.reloadList()))
						.subscribe({
							error: () => {
								this.notificationService.error($localize`Failed to delete the cost invoice.`)
							},
						})
				})
			}
		})
	}

	handleCheckboxesChanges(invoices: CostInvoiceRead[]) {
		this.selectedInvoices = invoices
	}

	handleCostInvoiceDetails(id: string): void {
		setLocalStorageScroll(id, this.scrollY, CostInvoiceListScrollStorageKey)
		this.loaderService.setContain().enable(async () => {
			await this.router.navigateByUrl(`/${config.modules.costInvoices.path}/${id}`)
		})
	}

	handleCostInvoiceUpdate(id: string): void {
		setLocalStorageScroll(id, this.scrollY, CostInvoiceListScrollStorageKey)
		this.loaderService.setContain().enable(async () => {
			await this.router.navigateByUrl(`/${config.modules.costInvoices.path}/${id}/update`)
		})
	}

	/**
	 * Issues a VAT invoice based on the cost of a given invoice.
	 *
	 * @param {CostInvoiceRead} invoice - The invoice object containing the cost information.
	 * @return {Promise<void>} - Resolves when the VAT invoice is issued successfully.
	 */
	async issueAVatInvoiceBasedOnTheCost(invoice: CostInvoiceRead): Promise<void> {
		await this.router.navigate([`/${config.modules.invoices.path}/based-on-the-cost/${invoice.id}`])
	}

	async addIncome(): Promise<void> {
		if (!this.selectedInvoices.length) {
			this.notificationService.error($localize`No cost invoice was selected`)
		} else {
			const next = async () => {
				const modal: ModalComponent<CreateBasedOnTheCostComponent> =
					await this.modalService.open<CreateBasedOnTheCostComponent>(CreateBasedOnTheCostComponent)
				modal.componentInstance.costInvoices = this.selectedInvoices
				modal.title = $localize`Add income`
				modal.iconName = 'add-revenue'
			}

			const assignedToInvoice = this.selectedInvoices.filter(elem => elem.is_assigned_to_invoice)

			if (assignedToInvoice.length) {
				const confirmation = await this.modalService.confirmation({
					title: $localize`Invoice Assignment Confirmation`,
					message: $localize`One or more of the selected cost invoices are already assigned to an incoming invoice. Are you sure you want to continue?`,
				})

				confirmation.subscribe(value => {
					if (value) {
						setTimeout(async () => {
							await next()
						}, 125)
					}
				})
			} else {
				await next()
			}
		}
	}

	async openChangeStatusModal(costInvoice: CostInvoiceRead) {
		const modal = await this.modalService.open(CostInvoiceUpdateStatusComponent)
		modal.title = $localize`Change cost invoice status`
		modal.iconName = 'status-change'
		modal.width = '400px'
		modal.componentInstance.costInvoice = costInvoice
		modal.componentInstance.statuses = this.paymentStatuses
		this.modalService.modalRef.closed.subscribe((updatedInvoice: CostInvoiceRead | boolean) => {
			if (updatedInvoice && typeof updatedInvoice !== 'boolean') {
				this.items = this.items.map(i =>
					i.id === updatedInvoice.id
						? {
								...i,
								payment_status: (updatedInvoice as any).payment_status ?? i.payment_status,
								payment_date: (updatedInvoice as any).payment_date,
								is_paid_cash: updatedInvoice.is_paid_cash,
							}
						: i,
				)
				this.invalidateInvoiceCache(updatedInvoice.id)
			}
		})
	}

	handleFilterSubmit(): void {
		this.pagination.reloadList()
	}

	isPredicted(costInvoice: CostInvoiceRead) {
		let isPredicted: boolean = false
		for (const item of costInvoice.items) {
			if (item.predicted_net_unit_price) {
				isPredicted = true
				break
			}
		}
		return isPredicted
	}

	getPredictedGrossValue(costInvoice: CostInvoiceRead): number {
		const netValueComponents: number[] = []
		const taxValueComponents: number[] = []
		for (const item of costInvoice.items) {
			const _netValue = calculateNetValueFromUnitNetPrice({
				netPrice: item.predicted_net_unit_price ?? 0,
				quantity: item.quantity ?? 1,
			})
			netValueComponents.push(_netValue)
			if (item.tax_rate?.value !== undefined && item.tax_rate?.value !== null) {
				taxValueComponents.push(
					calculateTaxValueFromNetValue({
						netValue: _netValue,
						taxRate: calculateDivision(item.tax_rate.value, 100),
					}),
				)
			}
		}
		const netValue = calculateSum(netValueComponents)
		const taxValue = calculateSum(taxValueComponents)
		return calculateSum([netValue, taxValue])
	}

	getDifference(costInvoice: CostInvoiceRead): number {
		return calculateDifference(this.getGrossValue(costInvoice.items), this.getPredictedGrossValue(costInvoice), 2)
	}

	showPredictedWarn(costInvoice: CostInvoiceRead): boolean {
		if (!this.isPredicted(costInvoice) || costInvoice.is_predicted) {
			return false
		} else {
			return this.getDifference(costInvoice) !== 0
		}
	}

	predictedTooltip(costInvoice: CostInvoiceRead): string {
		const str = $localize`The predicted costs do not match the actual costs!

The costs were predicted to be at the level of ${this.getPredictedGrossValue(costInvoice)} ${costInvoice.currency.toUpperCase()}, 
but the actual costs are ${this.getGrossValue(costInvoice.items)} ${costInvoice.currency.toUpperCase()}.`
		const str2 =
			this.getDifference(costInvoice) >= 0
				? $localize`\n\nThe invoice value is greater than predicted by ${this.getDifference(costInvoice)} ${costInvoice.currency.toUpperCase()}.`
				: $localize`\n\nThe invoice value is less than predicted by ${this.getDifference(costInvoice) * -1} ${costInvoice.currency.toUpperCase()}.`

		return str + str2
	}

	onSummaryCurrencyClick(currency: CurrencyCode): void {
		this.summaryCurrency = currency
	}

	aclCanReadContractors(): boolean {
		const permissions = [Permission.CONTRACTORS_READ]
		return this.aclService.hasPermission(permissions)
	}

	aclCanReadEntities(): boolean {
		const permissions = [Permission.ENTITIES_READ]
		return this.aclService.hasPermission(permissions)
	}

	fetchContractors(searchTerm?: string): void {
		this.contractorService.getContractors(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined).subscribe({
			next: (response: ContractorReadList) => {
				this.contractors = response
			},
			error: error => {
				if (error.code == 422) {
					console.error('invalid query on fetch request')
				}
				const errorMessage = $localize`Error during fetching contractors`
				this.notificationService.error(errorMessage)
			},
		})
	}

	fetchEntities(searchTerm?: string): void {
		if (!this.aclCanReadEntities()) {
			return
		}
		this.contractorService.getEntities(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined).subscribe({
			next: (response: EntityReadList) => {
				this.entities = response
			},
			error: error => {
				if (error.code == 422) {
					console.error('invalid query on fetch request')
				}
				const errorMessage = $localize`Error during fetching contractors`
				this.notificationService.error(errorMessage)
			},
		})
	}

	async openSelectInvoice(invoice: CostInvoiceRead) {
		if (invoice.is_group_invoice) {
			await this.router.navigateByUrl(`/${config.modules.costInvoices.path}/${invoice.id}/update`)
		} else {
			const modal = await this.modalService.open<AssignCostToOrder>(AssignCostToOrder)
			modal.title = $localize`Assign cost invoice` + ' ' + invoice.document_number
			modal.componentInstance.invoice = invoice
			modal.iconName = 'clip'
		}
	}

	confirmFilters(): void {
		this.pagination.reloadList()
	}

	setTableViewSortValue(): void {
		setTimeout(() => {
			if (!!this.tableComponent && !!this.sortByFormControl && !!this.sortOrderFormControl) {
				this.tableComponent.setTableSortValue(
					this.sortByFormControl.value ?? '',
					this.sortOrderFormControl.value ?? 'desc',
				)
			}
		}, 0)
	}

	handleQueryChanges(value: TableQuery): void {
		if (value.sort) {
			this.loaderService.setContain().enable(() => {
				this.sortByFormControl.patchValue((value.sort?.sortKey as SortBy) ?? null, { emitEvent: false })
				this.sortOrderFormControl.patchValue(value.sort?.sortDir ?? null, { emitEvent: false })
				this.pagination.reloadList()
			})
		} else {
			this.sortByFormControl.patchValue(null)
			this.sortOrderFormControl.patchValue(null)
		}
		this.loaderService.setContain().enable(this.fetchData.bind(this))
	}

	setCostInvoiceLocalStorageFiltersAndSort(): void {
		const filters = this.getLocalStorageFilterAndSortInvoice
		sessionStorage.setItem(
			CostInvoiceFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filtersFormGroup.value ?? null,
				sortOrder: null,
				sortBy: null,
				perPage: this.pagination.perPage,
				pageNumber: filters?.pageNumber ?? 1,
			}),
		)
	}

	setCostInvoiceLocalStoragePageNumber(): void {
		sessionStorage.setItem(
			CostInvoiceFiltersLocalStorageKey,
			JSON.stringify({
				filters: this.filtersFormGroup.value ?? null,
				sortOrder: this.sortOrderFormControl.value ?? null,
				sortBy: this.sortByFormControl.value ?? null,
				perPage: this.pagination.perPage,
				pageNumber: this.pagination.currentPage,
			}),
		)
	}

	checkIfContractorGroupSelected(contractor: ContractorRead) {
		if (this.filtersFormGroup.controls.contractor.value) {
			for (const filter of this.filtersFormGroup.controls.contractor.value) {
				if (contractor.id === filter.id) return true
			}
		}
		return false
	}

	checkIfContractorSelected(entity: EntityRead) {
		if (this.filtersFormGroup.controls.entity.value) {
			for (const filter of this.filtersFormGroup.controls.entity.value) {
				if (entity.id === filter.id) return true
			}
		}
		return false
	}

	goToOrder(order_id: string) {
		this.loaderService.enable(async () => {
			await this.router.navigateByUrl(`/${config.modules.orders.path}/${order_id}`)
		})
	}

	getLimitedOrders(orders_minimal: Array<OrderMinimalData>, id: string): Array<OrderMinimalData> {
		if (this.expandedRows[id]) {
			return orders_minimal
		}
		return orders_minimal.slice(0, 3)
	}

	showAll(id: string): void {
		this.expandedRows[id] = true
	}

	showLess(id: string): void {
		this.expandedRows[id] = false
	}

	aclCanReadPaymentStatus(): boolean {
		const permissions = [Permission.PAYMENT_STATUSES_READ]
		return this.aclService.hasPermission(permissions)
	}

	clearFilters() {
		sessionStorage.removeItem(CostInvoiceListScrollStorageKey)
	}

	resetDateControls() {
		this.dateControl.setValue(new Date())
		this.isPaidCashControl.setValue(false)
	}

	changePaymentDate(item: CostInvoiceRead) {
		this.dateControl.updateValueAndValidity()

		let date: string | null = null

		if (this.dateControl.value instanceof Date) {
			date = formatDate(this.dateControl.value)
		} else if (this.dateControl.value) {
			date = formatDate(new Date(this.dateControl.value))
		}

		this.invoicesService
			.patchCostInvoice(item.id, {
				payment_date: date ?? null,
				is_paid_cash: this.isPaidCashControl.value ?? false,
			})
			.subscribe({
				next: response => {
					this.items = this.items.map(i =>
						i.id === item.id
							? {
									...i,
									payment_date: response.payment_date,
									payment_status: (response as any).payment_status ?? i.payment_status,
									is_paid_cash: response.is_paid_cash,
								}
							: i,
					)
					this.invalidateInvoiceCache(item.id)
					this.notificationService.success($localize`Payment date has been changed`)
				},
				error: () => {
					this.notificationService.error($localize`Failed to change payment date`)
				},
			})
	}

	async toggleSentToAccounting(item: CostInvoiceRead) {
		const newValue = !item.sent_to_accounting
		const confirmation = await this.modalService.confirmation({
			title: newValue ? this.textMarkAsSentToAccounting : this.textUnmarkSentToAccounting,
			message: newValue ? this.textConfirmSentToAccounting : this.textConfirmUnmarkSentToAccounting,
		})

		confirmation.subscribe((value: boolean) => {
			if (value) {
				this.invoicesService
					.patchCostInvoice(item.id, {
						sent_to_accounting: newValue,
					})
					.subscribe({
						next: response => {
							this.items = this.items.map(i =>
								i.id === item.id
									? {
											...i,
											sent_to_accounting: response.sent_to_accounting,
										}
									: i,
							)
							this.notificationService.success(
								newValue
									? $localize`Invoice marked as sent to accounting`
									: $localize`Invoice unmarked as sent to accounting`,
							)
						},
						error: () => {
							this.notificationService.error(
								newValue
									? $localize`Failed to mark invoice as sent to accounting`
									: $localize`Failed to unmark invoice as sent to accounting`,
							)
						},
					})
			}
		})
	}

	async bulkMarkAsSentToAccounting() {
		const invoicesToMark = this.selectedInvoices.filter(i => !i.sent_to_accounting)

		if (!invoicesToMark.length) {
			this.notificationService.error(this.textNoInvoicesSelected)
			return
		}

		const confirmation = await this.modalService.confirmation({
			title: this.textBulkSendToAccounting,
			message: this.textConfirmBulkSendToAccounting,
		})

		confirmation.subscribe((value: boolean) => {
			if (value) {
				const requests = invoicesToMark.map(invoice =>
					this.invoicesService.patchCostInvoice(invoice.id, { sent_to_accounting: true }),
				)

				forkJoin(requests).subscribe({
					next: responses => {
						const updatedIds = new Set(responses.map(r => r.id))
						this.items = this.items.map(i =>
							updatedIds.has(i.id) ? { ...i, sent_to_accounting: true } : i,
						)
						this.tableComponent?.uncheckAllCheckboxes()
						this.selectedInvoices = []
						this.notificationService.success(this.textInvoicesMarkedAsSentToAccounting)
					},
					error: () => {
						this.notificationService.error(this.textFailedToMarkInvoicesAsSentToAccounting)
					},
				})
			}
		})
	}

	// Cached per-invoice total calculators to avoid heavy recomputation in template
	private getTotals(invoice: CostInvoiceRead): CostInvoiceTotalsCacheEntry {
		const cached = this.totalsCache.get(invoice.id)
		if (cached) return cached

		const net = invoice.items && invoice.items.length > 0 ? this.getNetValue(invoice.items) : 0
		const gross = invoice.items && invoice.items.length > 0 ? this.getGrossValue(invoice.items) : 0

		// compute predicted-related values only if any item has predicted price
		let isPredicted = false
		for (const it of invoice.items) {
			if (it.predicted_net_unit_price) {
				isPredicted = true
				break
			}
		}
		let predictedGross: number | undefined
		let difference: number | undefined
		if (isPredicted) {
			predictedGross = this.getPredictedGrossValue(invoice)
			difference = calculateDifference(gross, predictedGross, 2)
		}

		const entry: CostInvoiceTotalsCacheEntry = { net, gross }
		if (isPredicted) {
			entry.isPredicted = true
			entry.predictedGross = predictedGross
			entry.difference = difference
		}
		this.totalsCache.set(invoice.id, entry)
		return entry
	}

	private invalidateAllCaches(): void {
		this.totalsCache.clear()
		this.sumsByCurrency.clear()
	}

	private invalidateInvoiceCache(invoiceId: string): void {
		this.totalsCache.delete(invoiceId)
		this.sumsByCurrency.clear()
	}

	private ensureCurrencySums(currency: CurrencyCode): void {
		if (this.sumsByCurrency.has(currency)) return
		let net = 0
		let gross = 0
		for (const inv of this.items) {
			if (inv.currency !== currency) continue
			const totals = this.getTotals(inv)
			if (inv.items && inv.items.length > 0) {
				net += setDecimalPlaces(totals.net, 2)
				gross += setDecimalPlaces(totals.gross, 2)
			}
		}
		this.sumsByCurrency.set(currency, { net, gross })
	}

	private hasInvoices(): boolean | undefined {
		if (this.filtersFormGroup.controls.hasInvoices.value === 'INVOICES_THAT_ARE_COVERED_BY_REVENUES') {
			return true
		}
		if (this.filtersFormGroup.controls.hasInvoices.value === 'INVOICES_THAT_ARE_NOT_COVERED_BY_REVENUES') {
			return false
		}
		return undefined
	}

	/**
	 * Fetches data from the server using the invoicesService.
	 *
	 * @return {void}
	 */
	private fetchData(): void {
		this.loaderService.setContain().enable(() => {
			const filters = this.filtersFormGroup.value
			this.setCostInvoiceLocalStorageFiltersAndSort()
			this.filtersValue = JSON.parse(JSON.stringify(filters))

			let issueDateTo: string | undefined
			let issueDateFrom: string | undefined

			if (filters.issueDateFrom instanceof Date) {
				issueDateFrom = formatDate(filters.issueDateFrom)
			} else if (filters.issueDateFrom) {
				issueDateFrom = formatDate(new Date(filters.issueDateFrom))
			}
			if (filters.issueDateTo instanceof Date) {
				issueDateTo = formatDate(filters.issueDateTo)
			} else if (filters.issueDateTo) {
				issueDateTo = formatDate(new Date(filters.issueDateTo))
			}

			forkJoin({
				invoices: this.invoicesService.getCostInvoices(
					this.pagination.offset(),
					this.pagination.perPage,
					this.filtersFormGroup.controls.search.value ?? undefined,
					undefined,
					'desc',
					undefined,
					undefined,
					this.hasInvoices(),
					this.filtersFormGroup.controls.predictedCost.value ?? undefined,
					this.filtersFormGroup.controls.entity.value?.map(value => value.id) ?? undefined,
					this.filtersFormGroup.controls.contractor.value?.map(value => value.id) ?? undefined,
					this.filtersFormGroup.controls.paymentStatusID.value ?? undefined,
					issueDateFrom ?? undefined,
					issueDateTo ?? undefined,
					this.filtersFormGroup.controls.orderCost.value ?? undefined,
					this.filtersFormGroup.controls.isPaidCash.value ?? undefined,
					this.filtersFormGroup.controls.sentToAccounting.value ?? undefined,
				),
				paymentStatuses: this.invoicesService.getPaymentStatuses(),
			})
				.pipe(finalize(this.loaderService.disable.bind(this)))
				.subscribe({
					next: response => {
						this.pagination.setTotal(response.invoices.total)
						this.items = response.invoices.items
						if (!this.isLoaded) {
							const filters = this.getLocalStorageFilterAndSortInvoice
							if (filters) {
								this.pagination.setCurrentPage(filters.pageNumber ?? 1)
							}
							this.isLoaded = true
						}
						this.setCostInvoiceLocalStoragePageNumber()
						this.paymentStatuses = response.paymentStatuses
						this.invalidateAllCaches()
					},
					error: error => {
						console.error(error)
					},
				})
		})
	}

	private initFilters() {
		this.filtersFormGroup = new FormGroup<CostInvoiceFiltersFormScheme>({
			search: new FormControl<NullableString>(null),
			hasInvoices: new FormControl<NullableString>(null),
			predictedCost: new FormControl<NullableString>(null),
			entity: new FormControl<EntityRead[] | null>(null),
			contractor: new FormControl<ContractorRead[] | null>(null),
			paymentStatusID: new FormControl<NullableString>(null),
			issueDateFrom: new FormControl<NullableDate>(null),
			issueDateTo: new FormControl<NullableDate>(null),
			orderCost: new FormControl<NullableString>(null),
			isPaidCash: new FormControl<boolean | null>(null),
			sentToAccounting: new FormControl<boolean | null>(null),
		})
	}
}

export function setLocalStorageScroll(target: string, scrollY: number, key: string): void {
	sessionStorage.setItem(
		key,
		JSON.stringify({
			scrollY: scrollY,
			scrollTargetId: target,
		}),
	)
}
